import React, { useState } from "react";
import { User } from "../../../types";
import { ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Button from "../../ui/Button";
import StepBack from "../StepBack";

interface BodyStatsStepProps {
  age?: number;
  height?: number;
  weight?: number;
  onNext: (data: Partial<User>) => void;
  onBack: () => void;
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  suffix: string;
  index: number;
}

const InputField: React.FC<FieldProps> = ({ label, value, onChange, placeholder, suffix, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 * index, duration: 0.4 }}
    className="bg-surface p-4 rounded-2xl shadow-card border border-line focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-400 transition-all duration-300"
  >
    <label className="block text-xs font-bold text-content-tertiary uppercase tracking-widest mb-1">
      {label}
    </label>
    <div className="flex items-center justify-between">
      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xl font-bold text-content-primary outline-none placeholder:text-content-tertiary/40 bg-transparent"
      />
      <span className="text-sm font-bold text-content-tertiary">{suffix}</span>
    </div>
  </motion.div>
);

const BodyStatsStep: React.FC<BodyStatsStepProps> = ({
  age: initialAge,
  height: initialHeight,
  weight: initialWeight,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation();
  const [age, setAge] = useState<string>(initialAge ? initialAge.toString() : "");
  const [height, setHeight] = useState<string>(initialHeight ? initialHeight.toString() : "");
  const [weight, setWeight] = useState<string>(initialWeight ? initialWeight.toString() : "");

  const parsedAge = Number(age);
  const parsedHeight = Number(height);
  const parsedWeight = Number(weight);
  const isValid =
    parsedAge >= 13 &&
    parsedAge <= 90 &&
    parsedHeight >= 120 &&
    parsedHeight <= 230 &&
    parsedWeight >= 35 &&
    parsedWeight <= 250;

  const handleSubmit = () => {
    if (!isValid) return;
    onNext({
      age: parsedAge,
      height: parsedHeight,
      weight: parsedWeight,
    });
  };

  return (
    <div className="flex flex-col h-full px-4">
      <StepBack onBack={onBack} />

      <div className="mb-6 text-center">
        <h2 className="heading-display">{t("wizard.bodyStats.title")}</h2>
        <p className="text-sm text-content-tertiary font-medium mt-1">
          {t("wizard.bodyStats.subtitle")}
        </p>
      </div>

      <div className="space-y-3 flex-1 pb-8">
        <InputField
          label={t("wizard.bodyStats.age")}
          value={age}
          onChange={setAge}
          placeholder="25"
          suffix={t("wizard.bodyStats.ageSuffix")}
          index={0}
        />
        <InputField
          label={t("wizard.bodyStats.height")}
          value={height}
          onChange={setHeight}
          placeholder="175"
          suffix={t("wizard.bodyStats.heightSuffix")}
          index={1}
        />
        <InputField
          label={t("wizard.bodyStats.weight")}
          value={weight}
          onChange={setWeight}
          placeholder="70"
          suffix={t("wizard.bodyStats.weightSuffix")}
          index={2}
        />
        {age || height || weight ? (
          <p className={`text-xs font-medium ${isValid ? "text-content-tertiary" : "text-warning"}`}>
            {isValid ? t("wizard.bodyStats.rangeHint") : t("wizard.bodyStats.rangeError")}
          </p>
        ) : null}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-auto mb-[calc(2rem+var(--safe-bottom))]"
      >
        <Button variant="primary" size="lg" fullWidth disabled={!isValid} onClick={handleSubmit}>
          {t("wizard.bodyStats.continue")} <ChevronRight size={20} />
        </Button>
      </motion.div>
    </div>
  );
};

export default BodyStatsStep;
